import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { adminUsersApi } from '../api/adminUsers';
import { queryKeys } from '../lib/queryKeys';
import type { PaginationParams } from '../types/api';
import type { AdminUserInviteRequest, AdminUserUpdateRequest, AdminUserSuspendRequest, AdminSessionRevokeRequest } from '../types/adminUsers';

export function useAdminUsersList(params: PaginationParams & { role?: string; is_active?: boolean; search?: string }) {
  return useQuery({ queryKey: queryKeys.adminUsers.list(params), queryFn: () => adminUsersApi.list(params) });
}

export function useAdminUserDetail(id: string) {
  return useQuery({ queryKey: queryKeys.adminUsers.detail(id), queryFn: () => adminUsersApi.detail(id), enabled: !!id });
}

function useAdminUserMutation<T>(
  action: (args: T) => Promise<unknown>,
  msg: string,
  invalidateDetail?: boolean,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adminUsers.all });
      if (invalidateDetail) {
        qc.invalidateQueries({ queryKey: queryKeys.adminUsers.all });
      }
      message.success(msg);
    },
    onError: (err: unknown) => {
      const detail = (err as Record<string, string>)?.message || 'Operation failed';
      message.error(detail);
    },
  });
}

export function useInviteAdmin() {
  return useAdminUserMutation<AdminUserInviteRequest>(
    (data) => adminUsersApi.invite(data),
    'Admin invitation sent',
  );
}

export function useUpdateAdminRole() {
  return useAdminUserMutation<{ id: string; data: AdminUserUpdateRequest }>(
    ({ id, data }) => adminUsersApi.updateRole(id, data),
    'Admin role updated',
    true,
  );
}

export function useSuspendAdmin() {
  return useAdminUserMutation<{ id: string; data: AdminUserSuspendRequest }>(
    ({ id, data }) => adminUsersApi.suspend(id, data),
    'Admin suspended',
    true,
  );
}

export function useActivateAdmin() {
  return useAdminUserMutation<string>(
    (id) => adminUsersApi.activate(id),
    'Admin activated',
    true,
  );
}

export function useRemoveAdmin() {
  return useAdminUserMutation<string>(
    (id) => adminUsersApi.remove(id),
    'Admin privileges removed',
    true,
  );
}

export function useRevokeAdminSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ adminId, sessionId, data }: { adminId: string; sessionId: string; data: AdminSessionRevokeRequest }) =>
      adminUsersApi.revokeSession(adminId, sessionId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adminUsers.all });
      message.success('Session revoked');
    },
    onError: (err: unknown) => {
      const detail = (err as Record<string, string>)?.message || 'Failed to revoke session';
      message.error(detail);
    },
  });
}
